if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, timing } }) {
        if (!this.contains(event.target)) return;

        const interactionTiming = timing || {
          id: `fallback-${Date.now()}`,
          startedAt: performance.now(),
          targetId: target.id,
          targetValue: target.value,
        };
        interactionTiming.productInfoHandlerStart = performance.now();

        this.resetProductFormState();
        interactionTiming.afterResetProductFormState = performance.now();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);
        const localVariant = this.findLocalVariantForTarget(target);

        console.log('[variant-timing] product-info-start', {
          id: interactionTiming.id,
          targetId: interactionTiming.targetId,
          targetValue: interactionTiming.targetValue,
          productUrl,
          requestUrl,
          handlerDelayMs: +(interactionTiming.productInfoHandlerStart - interactionTiming.startedAt).toFixed(2),
          resetFormStateMs: +(interactionTiming.afterResetProductFormState - interactionTiming.productInfoHandlerStart).toFixed(2),
        });

        if (this.shouldUseLocalVariantSelection({ target, shouldSwapProduct, localVariant })) {
          this.applyLocalVariantSelection(localVariant, productUrl);
          window.setTimeout(() => {
            this.renderProductInfo({
              requestUrl,
              targetId: target.id,
              timing: interactionTiming,
              callback: shouldSwapProduct
                ? this.handleSwapProduct(productUrl, shouldFetchFullPage, interactionTiming)
                : this.handleUpdateProductInfo(productUrl, interactionTiming),
            });
          }, 0);
          return;
        }

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          timing: interactionTiming,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, interactionTiming)
            : this.handleUpdateProductInfo(productUrl, interactionTiming),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage, timing) {
        return (html) => {
          timing.beforeSwapCallback = performance.now();
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          timing.afterSwapCallback = performance.now();
          this.logVariantTiming('swap-product', timing, {
            variantId: variant?.id || null,
            updateFullPage,
          });
        };
      }

      renderProductInfo({ requestUrl, targetId, timing, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        timing.fetchStart = performance.now();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            timing.fetchResponse = performance.now();
            timing.responseStatus = response.status;
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            timing.responseTextReady = performance.now();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            timing.afterParse = performance.now();
            callback(html);
          })
          .then(() => {
            const shouldSkipFocus =
              this.dataset.localVariantSelection === 'true' &&
              window.matchMedia &&
              window.matchMedia('(max-width: 749px)').matches;

            if (shouldSkipFocus) {
              timing.afterFocus = performance.now();
              this.logVariantTiming('update-product-info', timing, { targetId, focusSkipped: true });
              return;
            }

            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            timing.afterFocus = performance.now();
            this.logVariantTiming('update-product-info', timing, { targetId });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('[variant-timing] fetch-aborted', {
                id: timing.id,
                targetId,
                elapsedMs: +(performance.now() - timing.startedAt).toFixed(2),
              });
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      syncOptionValues(html) {
        const sourceVariantSelects = html.querySelector('variant-selects');
        const destinationVariantSelects = this.variantSelectors;

        if (!sourceVariantSelects || !destinationVariantSelects) return;

        this.syncRadioOptionValues(destinationVariantSelects, sourceVariantSelects);
        this.syncSelectOptionValues(destinationVariantSelects, sourceVariantSelects);
      }

      syncRadioOptionValues(destinationVariantSelects, sourceVariantSelects) {
        const sourceInputs = sourceVariantSelects.querySelectorAll('fieldset input[type="radio"]');

        sourceInputs.forEach((sourceInput) => {
          const destinationInput = destinationVariantSelects.querySelector(`#${CSS.escape(sourceInput.id)}`);
          if (!destinationInput) return;

          destinationInput.checked = sourceInput.checked;
          destinationInput.className = sourceInput.className;
          destinationInput.disabled = sourceInput.disabled;

          ['data-product-url', 'data-option-value-id', 'data-variant-id'].forEach((attribute) => {
            const value = sourceInput.getAttribute(attribute);
            if (value === null) {
              destinationInput.removeAttribute(attribute);
            } else {
              destinationInput.setAttribute(attribute, value);
            }
          });

          const destinationLabel = destinationVariantSelects.querySelector(`label[for="${CSS.escape(sourceInput.id)}"]`);
          const sourceLabel = sourceVariantSelects.querySelector(`label[for="${CSS.escape(sourceInput.id)}"]`);
          if (!destinationLabel || !sourceLabel) return;

          destinationLabel.innerHTML = sourceLabel.innerHTML;
        });
      }

      syncSelectOptionValues(destinationVariantSelects, sourceVariantSelects) {
        const sourceSelects = sourceVariantSelects.querySelectorAll('select');

        sourceSelects.forEach((sourceSelect) => {
          const destinationSelect = destinationVariantSelects.querySelector(`#${CSS.escape(sourceSelect.id)}`);
          if (!destinationSelect) return;

          const selectedIndex = sourceSelect.selectedIndex;
          destinationSelect.innerHTML = sourceSelect.innerHTML;
          destinationSelect.selectedIndex = selectedIndex;

          const sourceSelectedValue = sourceSelect.closest('.product-form__input')?.querySelector('[data-selected-value]');
          const destinationSelectedValue = destinationSelect
            .closest('.product-form__input')
            ?.querySelector('[data-selected-value]');

          if (sourceSelectedValue && destinationSelectedValue) {
            destinationSelectedValue.innerHTML = sourceSelectedValue.innerHTML;
          }
        });
      }

      handleUpdateProductInfo(productUrl, timing) {
        return (html) => {
          timing.beforeUpdateCallback = performance.now();
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          timing.afterPickupAvailability = performance.now();
          this.syncOptionValues(html);
          timing.afterSyncOptionValues = performance.now();
          this.updateURL(productUrl, variant?.id);
          timing.afterUpdateURL = performance.now();
          this.updateVariantInputs(variant?.id);
          timing.afterUpdateVariantInputs = performance.now();

          if (!variant) {
            this.setUnavailable();
            timing.afterUnavailable = performance.now();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          timing.afterUpdateMedia = performance.now();

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          // Stock state shown beside the quantity picker, when that renders
          updateSourceFromDestination('Quantity-Availability');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          timing.afterSourceUpdates = performance.now();

          this.updateQuantityRules(this.sectionId, html);
          timing.afterQuantityRules = performance.now();
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          timing.afterSubmitButton = performance.now();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
          timing.afterVariantChangePublish = performance.now();
        };
      }

      logVariantTiming(phase, timing, extra = {}) {
        const marks = {
          startedAt: timing.startedAt,
          productInfoHandlerStart: timing.productInfoHandlerStart,
          afterResetProductFormState: timing.afterResetProductFormState,
          fetchStart: timing.fetchStart,
          fetchResponse: timing.fetchResponse,
          responseTextReady: timing.responseTextReady,
          afterParse: timing.afterParse,
          beforeUpdateCallback: timing.beforeUpdateCallback,
          afterPickupAvailability: timing.afterPickupAvailability,
          afterSyncOptionValues: timing.afterSyncOptionValues,
          afterUpdateURL: timing.afterUpdateURL,
          afterUpdateVariantInputs: timing.afterUpdateVariantInputs,
          afterUpdateMedia: timing.afterUpdateMedia,
          afterSourceUpdates: timing.afterSourceUpdates,
          afterQuantityRules: timing.afterQuantityRules,
          afterSubmitButton: timing.afterSubmitButton,
          afterVariantChangePublish: timing.afterVariantChangePublish,
          beforeSwapCallback: timing.beforeSwapCallback,
          afterSwapCallback: timing.afterSwapCallback,
          afterFocus: timing.afterFocus,
        };
        const durations = {};
        let lastMarkName = null;
        let lastMarkValue = null;

        Object.entries(marks).forEach(([name, value]) => {
          if (typeof value !== 'number') return;
          if (lastMarkValue !== null) {
            durations[`${lastMarkName}->${name}`] = +(value - lastMarkValue).toFixed(2);
          }
          lastMarkName = name;
          lastMarkValue = value;
        });

        const payload = {
          id: timing.id,
          responseStatus: timing.responseStatus,
          totalMs: typeof timing.afterFocus === 'number' ? +(timing.afterFocus - timing.startedAt).toFixed(2) : null,
          durations,
          ...extra,
        };

        window.__lastVariantTiming = payload;
        window.__lastVariantTimingByPhase = window.__lastVariantTimingByPhase || {};
        window.__lastVariantTimingByPhase[phase] = payload;

        console.log(`[variant-timing] ${phase}`, payload);
        console.log(`[variant-timing-json] ${phase} ${JSON.stringify(payload)}`);
      }

      shouldUseLocalVariantSelection({ target, shouldSwapProduct, localVariant }) {
        return this.dataset.localVariantSelection === 'true' && !shouldSwapProduct && !!localVariant && !!target.dataset.variantId;
      }

      get inlineVariants() {
        if (this._inlineVariants) return this._inlineVariants;

        const variantsJson = this.querySelector('[data-product-variants]')?.textContent;
        this._inlineVariants = variantsJson ? JSON.parse(variantsJson) : [];
        return this._inlineVariants;
      }

      findLocalVariantForTarget(target) {
        const variantId = target.dataset.variantId;
        if (!variantId) return null;

        return this.inlineVariants.find(({ id }) => id.toString() === variantId) || null;
      }

      applyLocalVariantSelection(variant, productUrl) {
        this.updateSelectedVariantJson(variant);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);
        this.productForm?.toggleSubmitButton(!(variant?.available), variant?.available ? undefined : window.variantStrings.soldOut);

        if (variant?.featured_media?.id) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true);
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant,
          },
        });
      }

      updateSelectedVariantJson(variant) {
        const selectedVariantScript = this.querySelector('variant-selects [data-selected-variant]');
        if (selectedVariantScript) selectedVariantScript.textContent = JSON.stringify(variant || null);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        if (this.querySelector('media-gallery').getAttribute('has_variant') === 'true') {
          const mediaGallerySource = this.querySelector('media-gallery');
          const mediaGalleryDestination = html.querySelector(`media-gallery`);
          mediaGallerySource.outerHTML = mediaGalleryDestination.outerHTML;
          return;
        }

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
